import { AnimatePresence, motion, useIsPresent, useReducedMotion } from "framer-motion";
import { CalendarDays, Plus, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

// "Your order in three steps": the one explanation a buyer gets, the first time
// they are inside a vendor's menu with nothing in their order
// (shared/buyerFirstRun.ts). Add items, choose a day (mind the cutoff), submit —
// how the app behaves, not instructions for the screens before it.
//
// Never write `bg-white` here: index.css forces width:100% on it under 640px.

interface IntroProps {
  vendorName: string;
  /** "delivery", "pickup" or "delivery or pickup" — what this buyer chooses between. */
  method: string;
  onDone: () => void;
}

export function OrderingIntro({ open, ...props }: IntroProps & { open: boolean }) {
  return <AnimatePresence>{open && <IntroSheet key="ordering-intro" {...props} />}</AnimatePresence>;
}

function IntroSheet({ vendorName, method, onDone }: IntroProps) {
  const reduceMotion = useReducedMotion();
  // False from the moment it starts closing: the sheet stops taking taps then,
  // so the buyer's next tap reaches the menu, not the sheet sliding away.
  const isPresent = useIsPresent();
  const steps = [
    {
      icon: Plus,
      title: "Add items",
      body: "Tap a category to see its items.",
    },
    {
      icon: CalendarDays,
      title: `Choose a ${method} day`,
      body: "Note the cutoff time: it's when you can last submit or change your order.",
    },
    {
      icon: Send,
      title: "Submit",
      body: "Review your items and submit your order.",
    },
  ];

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{ pointerEvents: isPresent ? "auto" : "none" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0.1 : 0.2 }}
      data-testid="ordering-intro"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onDone} aria-hidden="true" />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ordering-intro-title"
        className="relative w-full max-w-md rounded-t-2xl bg-[#fff] shadow-2xl px-6 pt-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
        initial={reduceMotion ? false : { y: "100%" }}
        animate={{ y: 0 }}
        exit={reduceMotion ? undefined : { y: "100%" }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
      >
        <h2 id="ordering-intro-title" className="text-xl font-semibold text-gray-900">
          Your order in three steps
        </h2>
        <p className="text-[15px] text-gray-600 mt-1 mb-5">Nothing goes to {vendorName} until you submit.</p>
        <ol className="space-y-5 mb-7">
          {steps.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-4">
              <span className="h-10 w-10 shrink-0 rounded-full bg-[#D6E5DB] flex items-center justify-center">
                <Icon className="h-5 w-5 text-cn-brand-green" />
              </span>
              <span>
                <span className="block font-semibold text-gray-900">{title}</span>
                <span className="block text-[15px] leading-snug text-gray-600 mt-0.5">{body}</span>
              </span>
            </li>
          ))}
        </ol>
        <Button
          type="button"
          onClick={onDone}
          className="w-full h-12 text-base font-semibold rounded-lg bg-cn-brand-green hover:bg-cn-brand-green-hover text-[#fff]"
          data-testid="ordering-intro-start"
        >
          Start ordering
        </Button>
      </motion.div>
    </motion.div>
  );
}
